import { IStats } from '@/types';
import { differenceInCalendarDays, lightFormat, parseISO } from 'date-fns';

type CarriedStreak = Pick<IStats, 'days_in_a_row' | 'multiplier' | 'used_vacation_days'>;

const blankDay = (usId: number, today: string): Omit<IStats, keyof CarriedStreak> => ({
  us_id: usId,
  today,
  new_problems: 0,
  learn_problems: 0,
  review_problems: 0,
  quick_looks: 0,
  minutes_watched: 0,
  goal_reached: 0,
  emptied_study_queue: 0,
  points: 0,
});

/** Streak, vacation days and multiplier carried over from the last day studied. */
function carryStreak(prev: IStats, now: Date): CarriedStreak {
  let daysInARow = prev.days_in_a_row;
  let usedVacationDays = prev.used_vacation_days;
  if (!prev.goal_reached) usedVacationDays += 1;
  usedVacationDays += differenceInCalendarDays(now, parseISO(prev.today)) - 1;
  if (Math.floor((daysInARow + 4) / 7) < usedVacationDays) {
    daysInARow = 0;
    usedVacationDays = 0;
  }
  const multiplier = (daysInARow < 2 ? 1 : Math.min(daysInARow, 5)) as IStats['multiplier'];
  return { days_in_a_row: daysInARow, used_vacation_days: usedVacationDays, multiplier };
}

/**
 * Stats rows (newest first) with a record for today on top, so the store can be set in one commit.
 * Today's record is the row dated the local date, even below a later-dated row. Returns rows itself when it is already on top.
 */
export function statsWithToday(rows: IStats[], usId: number, now: Date): IStats[] {
  const today = lightFormat(now, 'y-MM-dd');
  const at = rows.findIndex((r) => r.today === today);
  if (at === 0) return rows;
  if (at > 0) return [rows[at], ...rows.slice(0, at), ...rows.slice(at + 1)];
  const carried: CarriedStreak = rows.length
    ? carryStreak(rows[0], now)
    : { days_in_a_row: 0, used_vacation_days: 0, multiplier: 1 };
  return [{ ...blankDay(usId, today), ...carried }, ...rows];
}

const activity = (r: IStats): number =>
  r.new_problems + r.learn_problems + r.review_problems + r.quick_looks + r.minutes_watched;

/**
 * Loaded stats, keeping the store's today record when it has more activity: a studied or minutes-watched
 * reply that landed while the load was in flight is newer than the loaded rows. Returns loaded itself otherwise.
 */
export function keepNewerToday(loaded: IStats[], current: IStats[] | null | undefined): IStats[] {
  const mine = current && current[0];
  if (!mine || !loaded.length || mine.today !== loaded[0].today) return loaded;
  return activity(mine) > activity(loaded[0]) ? [mine, ...loaded.slice(1)] : loaded;
}
